import { CircleCheck, Lock } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/design-system/lib/cn';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import type { EditImpact } from '@escala/domain/eventEdit';
import type { Ministry } from '@escala/domain/types';
import type { EditChoices } from '@/store/AppStore';

const datas = (n: number) => `${n} ${n === 1 ? 'data' : 'datas'}`;
/** "1 data futura sem escala" / "3 datas futuras sem escala". */
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const pessoas = (n: number) => `${n} ${n === 1 ? 'pessoa escalada' : 'pessoas escaladas'}`;

/**
 * Confirmação da edição de uma série: as datas por grupo, o que muda em cada uma e a
 * escolha de aplicar às datas futuras que já têm escala e às passadas sem registro. As
 * passadas com quem serviu nunca perdem ninguém — só o título muda.
 */
export default function EditConfirmation({
  title,
  impact,
  choices,
  ministries,
  onChange,
}: {
  title: string;
  impact: EditImpact;
  choices: EditChoices;
  ministries: readonly Ministry[];
  onChange: (choices: EditChoices) => void;
}) {
  const { groups } = impact;
  const roleName = (roleId: string) =>
    ministries.flatMap((m) => m.roles).find((r) => r.id === roleId)?.name ?? 'Função';
  const ministryName = (id: string) => ministries.find((m) => m.id === id)?.name ?? 'Ministério';

  const changes: { key: string; text: string; danger?: boolean }[] = [
    ...impact.roles.flatMap((r) => [
      ...(r.increased
        ? [
            {
              key: `${r.roleId}-up`,
              text: `${roleName(r.roleId)}: de ${r.from} para ${r.qty} pessoas (vagas abertas em ${datas(r.increased)})`,
            },
          ]
        : []),
      ...(r.extras
        ? [
            {
              key: `${r.roleId}-extra`,
              text: `${roleName(r.roleId)}: ${pessoas(r.extras)} ficam como extras em ${datas(r.extraDates)}`,
            },
          ]
        : []),
      ...(r.removed
        ? [
            {
              key: `${r.roleId}-out`,
              text: `${roleName(r.roleId)} sai do evento: ${pessoas(r.removed)} serão removidas em ${datas(r.removedDates)}`,
              danger: true,
            },
          ]
        : []),
    ]),
    ...impact.ministries.map((m) => ({
      key: m.ministryId,
      text: `${ministryName(m.ministryId)} sai do evento: ${pessoas(m.removed)} serão removidas em ${datas(m.removedDates)}`,
      danger: true,
    })),
    ...(impact.unpublished
      ? [
          {
            key: 'unpublished',
            text: `${datas(impact.unpublished)} ${impact.unpublished === 1 ? 'publicada volta' : 'publicadas voltam'} a rascunho`,
          },
        ]
      : []),
  ];

  let scheduledDetail: ReactNode = (
    <span className="text-meta text-ink-muted">Essas datas ficam exatamente como estão.</span>
  );

  if (choices.includeScheduled && changes.length) {
    scheduledDetail = (
      <ul className="m-0 flex list-disc flex-col gap-0.5 pl-4.5">
        {changes.map((c) => (
          <li key={c.key} className={cn('text-meta', c.danger ? 'text-status-conflict' : 'text-ink-muted')}>
            {c.text}
          </li>
        ))}
      </ul>
    );
  } else if (choices.includeScheduled) {
    scheduledDetail = <span className="text-meta text-ink-muted">Ninguém sai da escala nessas datas.</span>;
  }

  return (
    <section aria-label="Confirmar a edição da série" className="flex flex-col gap-2.5">
      <p className="m-0 text-small text-ink-secondary">
        Confira onde as alterações de <strong>{title}</strong> serão aplicadas.
      </p>

      {groups.futureEmpty.length ? (
        <Row icon={<CircleCheck aria-hidden className="size-4.5 stroke-[1.8] text-status-complete" />}>
          <span className="flex-1 font-semibold">
            {count(groups.futureEmpty.length, 'data futura sem escala', 'datas futuras sem escala')}
          </span>
          <span className="text-caption text-ink-subtle">sempre incluídas</span>
        </Row>
      ) : null}

      {groups.futureScheduled.length ? (
        <Choice
          checked={choices.includeScheduled}
          label={count(
            groups.futureScheduled.length,
            'data futura que já tem escala',
            'datas futuras que já têm escala'
          )}
          onToggle={() => onChange({ ...choices, includeScheduled: !choices.includeScheduled })}
        >
          {scheduledDetail}
        </Choice>
      ) : null}

      {groups.pastEmpty.length ? (
        <Choice
          checked={choices.includePastEmpty}
          label={count(
            groups.pastEmpty.length,
            'data passada ainda sem registro',
            'datas passadas ainda sem registro'
          )}
          onToggle={() => onChange({ ...choices, includePastEmpty: !choices.includePastEmpty })}
        >
          <span className="text-meta text-ink-muted">
            {choices.includePastEmpty
              ? 'Recebem a nova composição para registrar quem serviu nelas.'
              : 'Essas datas ficam exatamente como estão.'}
          </span>
        </Choice>
      ) : null}

      {groups.pastServed.length ? (
        <Row icon={<Lock aria-hidden className="size-4.5 stroke-[1.8] text-ink-subtle" />}>
          <span className="flex flex-1 flex-col">
            <span className="font-semibold">
              {count(
                groups.pastServed.length,
                'data passada com histórico registrado',
                'datas passadas com histórico registrado'
              )}
            </span>
            <span className="text-meta text-ink-muted">Quem serviu é mantido. Só o título é atualizado.</span>
          </span>
        </Row>
      ) : null}
    </section>
  );
}

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-control border border-line-divider px-3 py-2.5 text-small">
      <span className="mt-0.5">{icon}</span>
      {children}
    </div>
  );
}

function Choice({
  checked,
  label,
  onToggle,
  children,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-control border border-line-divider px-3 py-2.5 text-small">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className="flex items-center gap-2.5 text-left font-semibold"
      >
        <CheckboxMark checked={checked} />
        {label}
      </button>
      <div className="pl-7">{children}</div>
    </div>
  );
}
